import React, { useState } from "react";
import COMUNAS_DATA from "../../lib/comunas.json";
import { Plus, Trash2, Edit, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  FK, type as T, Field, Row, TextInput, SuffixInput, Select, EstadoRow, Pill,
  FkPrimary, FkGhost, FkOutline,
} from "../product_config/formkit";

const CODIGO_A_NOMBRE = new Map(COMUNAS_DATA.map((c) => [c.codigo, c.nombre]));
const NOMBRE_A_CODIGO = new Map(COMUNAS_DATA.map((c) => [c.nombre.toLowerCase(), c.codigo]));

const SEXO_OPTS = [{ value: "M", label: "Masculino" }, { value: "F", label: "Femenino" }, { value: "U", label: "Unisex" }];
const CARGA_OPTS = [1, 2, 3, 4, 5].map((n) => ({ value: `carga_${n}`, label: `Carga ${n}` }));
const MONEDA_OPTS = [{ value: "UF", label: "UF" }, { value: "CLP", label: "CLP" }];
const FRECUENCIA_OPTS = [{ value: "mensual", label: "Mensual (cuotas)" }, { value: "anual", label: "Anual (pago único)" }];

// Etiqueta legible del programa de cobro. Un precio mensual activo sin
// n_cuotas se marca en vez de mostrar un 12 que nadie configuró: flow lo
// asumiría, y el cliente vería ese programa inventado al pagar.
const cobroLabel = (precio) => {
  const frec = precio?.frecuencia || "mensual";
  if (frec === "anual") return "Anual · 1 cuota (pago único)";
  if (!precio?.n_cuotas) return "Mensual · sin cuotas configuradas";
  const n = precio.n_cuotas;
  return `Mensual · ${n} ${n === 1 ? "cuota" : "cuotas"}`;
};
const CONSTR_OPTS = [{ value: "Solida", label: "Sólida" }, { value: "Mixta", label: "Mixta" }];
const RIESGO_OPTS = [{ value: "A", label: "A — Bajo riesgo" }, { value: "B", label: "B — Riesgo medio" }, { value: "C", label: "C — Alto riesgo" }];

const iconBtnStyle = { appearance: "none", cursor: "pointer", display: "grid", placeItems: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid " + FK.border, background: FK.surface, color: FK.sub };

function IconBtn({ icon: Icon, onClick, title, danger }) {
  return (
    <button type="button" onClick={onClick} title={title} style={{ ...iconBtnStyle, color: danger ? FK.err : FK.sub }}>
      <Icon size={15} />
    </button>
  );
}

function Stat({ label, children, bold }) {
  return (
    <div>
      <div style={T.label}>{label}</div>
      <p style={{ margin: "5px 0 0", fontSize: 14, color: FK.text, fontWeight: bold ? 700 : 400 }}>{children}</p>
    </div>
  );
}

function ComunasInput({ comunas = [], onChange }) {
  const [input, setInput] = useState("");
  const nombreACod = (name) => NOMBRE_A_CODIGO.get(name.toLowerCase().trim()) ?? null;

  const addMany = (raw) => {
    const nuevas = raw
      .split(/[\n,;]+/)
      .map((s) => {
        const trimmed = s.trim();
        if (CODIGO_A_NOMBRE.has(trimmed)) return trimmed;
        return nombreACod(trimmed) ?? trimmed;
      })
      .filter((s) => s && !comunas.includes(s));
    if (nuevas.length) onChange([...comunas, ...nuevas]);
  };

  const add = () => { addMany(input); setInput(""); };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (/[\n,;]/.test(text)) {
      e.preventDefault();
      addMany(text);
      setInput("");
    }
  };

  const remove = (codigo) => onChange(comunas.filter((x) => x !== codigo));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <TextInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onPaste={handlePaste}
          placeholder="Escribe o pega comunas (coma, punto y coma o salto de línea)"
          list="comunas-datalist"
        />
        <datalist id="comunas-datalist">
          {COMUNAS_DATA.map((c) => (<option key={c.codigo} value={c.nombre} />))}
        </datalist>
        <FkOutline onClick={add}><Plus size={14} /></FkOutline>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {comunas.map((codigo) => {
          const nombre = CODIGO_A_NOMBRE.get(codigo);
          const ok = Boolean(nombre);
          return (
            <span
              key={codigo}
              onClick={() => remove(codigo)}
              title={ok ? `${nombre} (${codigo})` : `Código "${codigo}" no encontrado en listado oficial`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 8, fontSize: 12.5, cursor: "pointer", border: "1px solid " + (ok ? FK.border : FK.errBorder), background: ok ? FK.surface : FK.errBg, color: ok ? FK.text : FK.err }}
            >
              {nombre ?? codigo} <X size={12} />
            </span>
          );
        })}
      </div>
      {comunas.some((c) => !CODIGO_A_NOMBRE.has(c)) && (
        <p style={{ margin: 0, fontSize: 12, color: FK.err }}>
          Las comunas en rojo tienen códigos no reconocidos — los usuarios no podrán seleccionarlas.
        </p>
      )}
    </div>
  );
}

// Programa de cobro (frecuencia / n° cuotas / moneda) — aplica a todos los
// tipos de calculadora; es lo que flow y payment leen para armar las cuotas.
function CobroFields({ precio, onChange }) {
  const frecuencia = precio?.frecuencia || "mensual";
  return (
    <Row>
      <Field label="Frecuencia de cobro" hint="Mensual = suscripción en cuotas; Anual = pago único">
        <Select value={frecuencia} options={FRECUENCIA_OPTS} onChange={(e) => onChange({ ...precio, frecuencia: e.target.value })} />
      </Field>
      <Field
        label={frecuencia === "anual" ? "N° de cuotas" : "N° de cuotas *"}
        hint={frecuencia === "anual" ? "Un pago único es siempre 1 cobro" : "Requerido: es el programa que ve el cliente al pagar"}
      >
        <TextInput type="number" min="1" max="60" disabled={frecuencia === "anual"} value={frecuencia === "anual" ? "" : (precio?.n_cuotas ?? "")} onChange={(e) => onChange({ ...precio, n_cuotas: e.target.value })} placeholder={frecuencia === "anual" ? "1" : "12"} />
      </Field>
      <Field label="Moneda">
        <Select value={precio?.moneda || "UF"} options={MONEDA_OPTS} onChange={(e) => onChange({ ...precio, moneda: e.target.value })} />
      </Field>
    </Row>
  );
}

function ConstruccionComunaFields({ precio, onChange }) {
  const atributos = precio.atributos || {};
  const setAtributo = (key, val) => onChange({ ...precio, atributos: { ...atributos, [key]: val } });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Row>
        <Field label="Tipo de construcción" required>
          <Select value={atributos.tipo_construccion || ""} placeholder="Selecciona tipo" options={CONSTR_OPTS} onChange={(e) => setAtributo("tipo_construccion", e.target.value)} />
        </Field>
        <Field label="Riesgo">
          <Select value={atributos.riesgo || ""} placeholder="A / B / C" options={RIESGO_OPTS} onChange={(e) => setAtributo("riesgo", e.target.value)} />
        </Field>
        <Field label="Precio UF" required>
          <SuffixInput type="number" value={precio.precio_uf || ""} onChange={(e) => onChange({ ...precio, precio_uf: e.target.value })} placeholder="0,00" suffix="UF" />
        </Field>
      </Row>
      <Field label="Comunas cubiertas" hint="Agrupa comunas con el mismo precio. Click en la comuna para eliminarla." full>
        <ComunasInput comunas={atributos.comunas || []} onChange={(arr) => setAtributo("comunas", arr)} />
      </Field>
      <EstadoRow titulo="Activo" texto="El tier participa en el cálculo." on={precio?.Activo ?? true} onChange={(v) => onChange({ ...precio, Activo: v })} />
    </div>
  );
}

function MontoTasaFields({ precio, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Row>
        <Field label="Tasa" required hint="Prima = monto asegurado (UF) × tasa. Ej: 0,03">
          <TextInput type="number" value={precio.tasa ?? ""} onChange={(e) => onChange({ ...precio, tasa: e.target.value })} placeholder="0,03" />
        </Field>
        <Field label="Monto mínimo" hint="Valor mínimo asegurable (deja vacío = sin mínimo)">
          <SuffixInput type="number" value={precio.monto_minimo ?? ""} onChange={(e) => onChange({ ...precio, monto_minimo: e.target.value })} placeholder="5" suffix="UF" />
        </Field>
        <Field label="Monto máximo" hint="Valor máximo asegurable (deja vacío = sin máximo)">
          <SuffixInput type="number" value={precio.monto_maximo ?? ""} onChange={(e) => onChange({ ...precio, monto_maximo: e.target.value })} placeholder="300" suffix="UF" />
        </Field>
      </Row>
      <EstadoRow titulo="Activo" texto="El tier participa en el cálculo." on={precio?.Activo ?? true} onChange={(v) => onChange({ ...precio, Activo: v })} />
    </div>
  );
}

export default function PreciosBaseTab({
  preciosBase,
  setPreciosBase,
  newPrecio,
  setNewPrecio,
  showPreciosForm,
  setShowPreciosForm,
  handleAddPrecio,
  handleRemovePrecio,
  preciosCarga,
  setPreciosCarga,
  cargasPricingMode,
  calculatorType,
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingPrecio, setEditingPrecio] = useState({});
  const [editingCargaId, setEditingCargaId] = useState(null);
  const [editingCarga, setEditingCarga] = useState({});
  const [showCargasForm, setShowCargasForm] = useState({});
  const [newCargaByPrecio, setNewCargaByPrecio] = useState({});

  const handleEditPrecio = (index) => {
    setEditingIndex(index);
    setEditingPrecio({ ...preciosBase[index] });
  };

  const handleSavePrecio = () => {
    const updatedPrecios = [...preciosBase];
    updatedPrecios[editingIndex] = editingPrecio;
    setPreciosBase(updatedPrecios);
    setEditingIndex(null);
    setEditingPrecio({});
    toast.success('Precio actualizado exitosamente');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingPrecio({});
  };

  const handleShowCargaForm = (precioId) => {
    setShowCargasForm(prev => ({ ...prev, [precioId]: true }));
    setNewCargaByPrecio(prev => ({
      ...prev,
      [precioId]: { tipo_carga: "", precio_uf: "", moneda: "UF", activo: true }
    }));
  };

  const handleAddCarga = (precio) => {
    const precioId = precio?.id;
    const newCarga = newCargaByPrecio?.[precioId];

    if (!newCarga?.tipo_carga || !newCarga?.precio_uf) {
      toast.error('Tipo de carga y precio UF son requeridos');
      return;
    }

    const newId = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    setPreciosCarga(prev => ([
      ...prev,
      { ...newCarga, id: newId, precio_base_id: precioId, plan_id: precio?.plan_id || null }
    ]));

    setNewCargaByPrecio(prev => ({
      ...prev,
      [precioId]: { tipo_carga: "", precio_uf: "", moneda: "UF", activo: true }
    }));
    setShowCargasForm(prev => ({ ...prev, [precioId]: false }));
  };

  const handleEditCarga = (carga) => {
    setEditingCargaId(carga?.id || null);
    setEditingCarga({ ...carga });
  };

  const handleSaveCarga = () => {
    if (!editingCargaId) return;
    setPreciosCarga(prev => prev.map(item => (item?.id === editingCargaId ? editingCarga : item)));
    setEditingCargaId(null);
    setEditingCarga({});
    toast.success('Precio de carga actualizado exitosamente');
  };

  const handleCancelEditCarga = () => {
    setEditingCargaId(null);
    setEditingCarga({});
  };

  const handleRemoveCarga = (cargaId) => {
    setPreciosCarga(prev => prev.filter((item) => item?.id !== cargaId));
  };

  const cardStyle = { border: "1px solid " + FK.border, borderRadius: 12, background: FK.surface, padding: 16 };
  const subCardStyle = { border: "1px solid " + FK.border, borderRadius: 12, background: FK.field, padding: 16 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Botón nuevo precio */}
      {!showPreciosForm && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <FkPrimary onClick={() => setShowPreciosForm(true)}><Plus size={16} /> Nuevo precio base</FkPrimary>
        </div>
      )}

      {/* Form nuevo precio */}
      {showPreciosForm && (
        <div style={subCardStyle}>
          <div style={{ ...T.sectionTitle, marginBottom: 16 }}>Nuevo precio base</div>
          {calculatorType === "construccion_comuna" ? (
            <ConstruccionComunaFields precio={newPrecio} onChange={setNewPrecio} />
          ) : calculatorType === "monto_tasa" ? (
            <MontoTasaFields precio={newPrecio} onChange={setNewPrecio} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Row>
                <Field label="Tramo etario" htmlFor="new_precio_tramo_etario">
                  <TextInput id="new_precio_tramo_etario" value={newPrecio.tramo_etario} onChange={(e) => setNewPrecio(prev => ({ ...prev, tramo_etario: e.target.value }))} placeholder="Ej: 18-25" />
                </Field>
                <Field label="Sexo" htmlFor="new_precio_sexo">
                  <Select id="new_precio_sexo" value={newPrecio.sexo} placeholder="Selecciona sexo" options={SEXO_OPTS} onChange={(e) => setNewPrecio(prev => ({ ...prev, sexo: e.target.value }))} />
                </Field>
                <Field label="Precio UF" required htmlFor="new_precio_uf">
                  <SuffixInput id="new_precio_uf" type="number" value={newPrecio.precio_uf} onChange={(e) => setNewPrecio(prev => ({ ...prev, precio_uf: e.target.value }))} placeholder="0,00" suffix="UF" />
                </Field>
              </Row>
              <Row>
                <Field label="Edad mínima" htmlFor="new_precio_edad_min">
                  <TextInput id="new_precio_edad_min" type="number" value={newPrecio.edad_minima} onChange={(e) => setNewPrecio(prev => ({ ...prev, edad_minima: e.target.value }))} placeholder="18" />
                </Field>
                <Field label="Edad máxima" htmlFor="new_precio_edad_max">
                  <TextInput id="new_precio_edad_max" type="number" value={newPrecio.edad_maxima} onChange={(e) => setNewPrecio(prev => ({ ...prev, edad_maxima: e.target.value }))} placeholder="65" />
                </Field>
                <Field label="Estado" htmlFor="new_precio_activo">
                  <EstadoRow titulo="Activo" texto="Participa en el cálculo." on={newPrecio?.Activo ?? true} onChange={(checked) => setNewPrecio(prev => ({ ...prev, Activo: checked }))} />
                </Field>
              </Row>
            </div>
          )}
          <div style={{ marginTop: 18 }}>
            <CobroFields precio={newPrecio} onChange={setNewPrecio} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <FkPrimary onClick={handleAddPrecio}>Agregar precio</FkPrimary>
            <FkGhost onClick={() => setShowPreciosForm(false)}>Cancelar</FkGhost>
          </div>
        </div>
      )}

      {/* Lista de precios */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={T.label}>Precios configurados</div>
        {preciosBase.length === 0 ? (
          <p style={{ textAlign: "center", padding: "28px 0", fontSize: 13, color: FK.muted, margin: 0 }}>No hay precios configurados.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {preciosBase.map((precio, index) => (
              <div key={index} style={cardStyle}>
                {editingIndex === index ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {calculatorType === "construccion_comuna" ? (
                      <ConstruccionComunaFields precio={editingPrecio} onChange={setEditingPrecio} />
                    ) : calculatorType === "monto_tasa" ? (
                      <MontoTasaFields precio={editingPrecio} onChange={setEditingPrecio} />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        <Row>
                          <Field label="Tramo etario">
                            <TextInput value={editingPrecio.tramo_etario || ''} onChange={(e) => setEditingPrecio(prev => ({ ...prev, tramo_etario: e.target.value }))} placeholder="Ej: 18-25" />
                          </Field>
                          <Field label="Sexo">
                            <Select value={editingPrecio.sexo || ''} placeholder="Selecciona sexo" options={SEXO_OPTS} onChange={(e) => setEditingPrecio(prev => ({ ...prev, sexo: e.target.value }))} />
                          </Field>
                          <Field label="Precio UF">
                            <SuffixInput type="number" value={editingPrecio.precio_uf || ''} onChange={(e) => setEditingPrecio(prev => ({ ...prev, precio_uf: e.target.value }))} placeholder="0,00" suffix="UF" />
                          </Field>
                        </Row>
                        <Row>
                          <Field label="Edad mínima">
                            <TextInput type="number" value={editingPrecio.edad_minima || ''} onChange={(e) => setEditingPrecio(prev => ({ ...prev, edad_minima: e.target.value }))} placeholder="18" />
                          </Field>
                          <Field label="Edad máxima">
                            <TextInput type="number" value={editingPrecio.edad_maxima || ''} onChange={(e) => setEditingPrecio(prev => ({ ...prev, edad_maxima: e.target.value }))} placeholder="65" />
                          </Field>
                          <Field label="Estado">
                            <EstadoRow titulo="Activo" texto="Participa en el cálculo." on={editingPrecio?.Activo ?? false} onChange={(checked) => setEditingPrecio(prev => ({ ...prev, Activo: checked }))} />
                          </Field>
                        </Row>
                      </div>
                    )}
                    <CobroFields precio={editingPrecio} onChange={setEditingPrecio} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <FkPrimary onClick={handleSavePrecio}><Save size={16} /> Guardar</FkPrimary>
                      <FkGhost onClick={handleCancelEdit}><X size={16} /> Cancelar</FkGhost>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {calculatorType === "construccion_comuna" ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
                              <Stat label="Tipo construcción">{precio.atributos?.tipo_construccion || 'N/A'}</Stat>
                              <Stat label="Riesgo">{precio.atributos?.riesgo || 'N/A'}</Stat>
                              <Stat label="Precio UF" bold>{precio.precio_uf}</Stat>
                              <Stat label="Cobro">{cobroLabel(precio)}</Stat>
                              <Stat label="Estado">{precio?.Activo ? <Pill tone="ok">Activo</Pill> : <Pill tone="muted">Inactivo</Pill>}</Stat>
                            </div>
                            <div>
                              <div style={T.label}>Comunas ({(precio.atributos?.comunas || []).length})</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                {(precio.atributos?.comunas || []).map((c) => (
                                  <Pill key={c} tone="muted">{CODIGO_A_NOMBRE.get(c) ?? c}</Pill>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : calculatorType === "monto_tasa" ? (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
                            <Stat label="Tasa" bold>{precio.tasa ?? 'N/A'}</Stat>
                            <Stat label="Monto mínimo">{precio.monto_minimo != null && precio.monto_minimo !== '' ? `UF ${precio.monto_minimo}` : 'Sin mínimo'}</Stat>
                            <Stat label="Monto máximo">{precio.monto_maximo != null && precio.monto_maximo !== '' ? `UF ${precio.monto_maximo}` : 'Sin máximo'}</Stat>
                            <Stat label="Cobro">{cobroLabel(precio)}</Stat>
                            <Stat label="Estado">{precio?.Activo ? <Pill tone="ok">Activo</Pill> : <Pill tone="muted">Inactivo</Pill>}</Stat>
                          </div>
                        ) : (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
                            <Stat label="Tramo etario">{precio.tramo_etario || 'N/A'}</Stat>
                            <Stat label="Sexo">{precio.sexo || 'N/A'}</Stat>
                            <Stat label="Precio UF" bold>{precio.precio_uf}</Stat>
                            <Stat label="Cobro">{cobroLabel(precio)}</Stat>
                            <Stat label="Estado">{precio?.Activo ? <Pill tone="ok">Activo</Pill> : <Pill tone="muted">Inactivo</Pill>}</Stat>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <IconBtn icon={Edit} title="Editar" onClick={() => handleEditPrecio(index)} />
                        <IconBtn icon={Trash2} title="Eliminar" danger onClick={() => handleRemovePrecio(index)} />
                      </div>
                    </div>

                    {/* Cargas */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid " + FK.line, paddingTop: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: FK.text }}>
                          Precios de cargas <span style={{ color: FK.muted, fontWeight: 400 }}>(modo {cargasPricingMode || "no definido"})</span>
                        </span>
                        {typeof precio?.id === 'string' ? (
                          <FkOutline onClick={() => handleShowCargaForm(precio.id)}><Plus size={14} /> Nuevo precio de carga</FkOutline>
                        ) : (
                          <span style={{ fontSize: 12, color: FK.muted }}>Guarda el precio base para agregar cargas</span>
                        )}
                      </div>

                      {showCargasForm?.[precio?.id] && (
                        <div style={subCardStyle}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                            <Row>
                              <Field label="Tipo de carga" required>
                                <Select value={newCargaByPrecio?.[precio?.id]?.tipo_carga || ""} placeholder="Selecciona tipo" options={CARGA_OPTS}
                                  onChange={(e) => setNewCargaByPrecio(prev => ({ ...prev, [precio?.id]: { ...prev?.[precio?.id], tipo_carga: e.target.value } }))} />
                              </Field>
                              <Field label="Precio UF" required>
                                <SuffixInput type="number" value={newCargaByPrecio?.[precio?.id]?.precio_uf || ""} placeholder="0,00" suffix="UF"
                                  onChange={(e) => setNewCargaByPrecio(prev => ({ ...prev, [precio?.id]: { ...prev?.[precio?.id], precio_uf: e.target.value } }))} />
                              </Field>
                              <Field label="Moneda">
                                <Select value={newCargaByPrecio?.[precio?.id]?.moneda || "UF"} options={MONEDA_OPTS}
                                  onChange={(e) => setNewCargaByPrecio(prev => ({ ...prev, [precio?.id]: { ...prev?.[precio?.id], moneda: e.target.value } }))} />
                              </Field>
                            </Row>
                            <EstadoRow titulo="Activo" texto="La carga participa en el cálculo." on={newCargaByPrecio?.[precio?.id]?.activo ?? true}
                              onChange={(checked) => setNewCargaByPrecio(prev => ({ ...prev, [precio?.id]: { ...prev?.[precio?.id], activo: checked } }))} />
                            <div style={{ display: "flex", gap: 8 }}>
                              <FkPrimary onClick={() => handleAddCarga(precio)}>Agregar precio de carga</FkPrimary>
                              <FkGhost onClick={() => setShowCargasForm(prev => ({ ...prev, [precio?.id]: false }))}>Cancelar</FkGhost>
                            </div>
                          </div>
                        </div>
                      )}

                      {preciosCarga
                        .map((carga, cargaIndex) => ({ carga, cargaIndex }))
                        .filter(({ carga }) => carga?.precio_base_id === precio?.id).length === 0 ? (
                          <p style={{ margin: 0, fontSize: 12.5, color: FK.muted }}>No hay precios de cargas.</p>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {preciosCarga
                              .map((carga, cargaIndex) => ({ carga, cargaIndex }))
                              .filter(({ carga }) => carga?.precio_base_id === precio?.id)
                              .map(({ carga, cargaIndex }) => (
                                <div key={carga?.id || `${precio?.id}-${cargaIndex}`} style={subCardStyle}>
                                  {editingCargaId && carga?.id === editingCargaId ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                      <Row>
                                        <Field label="Tipo de carga">
                                          <Select value={editingCarga?.tipo_carga || ""} placeholder="Selecciona tipo" options={CARGA_OPTS}
                                            onChange={(e) => setEditingCarga(prev => ({ ...prev, tipo_carga: e.target.value }))} />
                                        </Field>
                                        <Field label="Precio UF">
                                          <SuffixInput type="number" value={editingCarga?.precio_uf || ""} placeholder="0,00" suffix="UF"
                                            onChange={(e) => setEditingCarga(prev => ({ ...prev, precio_uf: e.target.value }))} />
                                        </Field>
                                        <Field label="Moneda">
                                          <Select value={editingCarga?.moneda || "UF"} options={MONEDA_OPTS}
                                            onChange={(e) => setEditingCarga(prev => ({ ...prev, moneda: e.target.value }))} />
                                        </Field>
                                      </Row>
                                      <EstadoRow titulo="Activo" texto="La carga participa en el cálculo." on={editingCarga?.activo ?? false}
                                        onChange={(checked) => setEditingCarga(prev => ({ ...prev, activo: checked }))} />
                                      <div style={{ display: "flex", gap: 8 }}>
                                        <FkPrimary onClick={handleSaveCarga}><Save size={16} /> Guardar</FkPrimary>
                                        <FkGhost onClick={handleCancelEditCarga}><X size={16} /> Cancelar</FkGhost>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 16, flex: 1 }}>
                                        <Stat label="Tipo de carga">{carga?.tipo_carga || 'N/A'}</Stat>
                                        <Stat label="Precio UF" bold>{carga?.precio_uf || '-'}</Stat>
                                        <Stat label="Moneda">{carga?.moneda || 'UF'}</Stat>
                                        <Stat label="Estado">{carga?.activo ? <Pill tone="ok">Activo</Pill> : <Pill tone="muted">Inactivo</Pill>}</Stat>
                                      </div>
                                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                        <IconBtn icon={Edit} title="Editar" onClick={() => handleEditCarga(carga)} />
                                        <IconBtn icon={Trash2} title="Eliminar" danger onClick={() => handleRemoveCarga(carga?.id)} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
